import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { IdentityService } from 'src/app/core/identity.service';
import { APP_CONFIG, Settings } from '../../../config/settings';
import { combine } from '../../utilities/route';
import { AtmUserCredential } from './models/atm-user-credential.model';
import { AtmFacility } from './models/atm-facility.model';
import { AtmSystem } from './models/atm-system.model';
import { AtmVms } from './models/atm-vms.model';
import { AtmTargetPercentage } from './models/atm-target-percentage.model';
import { AtmProfession } from './models/atm-profession.model';
import { AtmExpertise } from './models/atm-expertise.model';
import { AdjustedTargetMarginLevelType } from '../../../recruiting/atm/models/adjusted-target-margin-level.model';

@Injectable({
    providedIn: 'root'
})
export class AtmService {
    private coreApiPath: string;

    constructor(
        @Inject(APP_CONFIG) settings: Settings,
        private _http: HttpClient,
        private _identityService: IdentityService
    ) {
        this.coreApiPath = combine(settings.coreAPIUrl, 'ayanova');
    }

    isUserAuthorized(): Observable<AtmUserCredential> {
        return this._http.get<AtmUserCredential>(`${this.coreApiPath}/atm/user-credential`);
    }

    getFacilityNames(name: string, count: number): Observable<AtmFacility[]> {
        return this._http.get<AtmFacility[]>(
            `${this.coreApiPath}/atm/facility-names?name=${encodeURIComponent(name)}&count=${count}`
        );
    }

    getSystemNames(name: string, count: number): Observable<AtmSystem[]> {
        return this._http.get<AtmSystem[]>(
            `${this.coreApiPath}/atm/system-names?name=${encodeURIComponent(name)}&count=${count}`
        );
    }

    getVmsNames(name: string, count: number): Observable<AtmVms[]> {
        return this._http.get<AtmVms[]>(
            `${this.coreApiPath}/atm/vms-names?name=${encodeURIComponent(name)}&count=${count}`
        );
    }

    getAtmProfessions(): Observable<AtmProfession[]> {
        return this._http.get<AtmProfession[]>(`${this.coreApiPath}/atm/professions`);
    }

    getAtmExpertises(professionId: number): Observable<AtmExpertise[]> {
        return this._http.get<AtmExpertise[]>(`${this.coreApiPath}/atm/professions/${professionId}/expertises`);
    }

    getAtm(type: string, request: AtmTargetPercentage): Observable<AtmTargetPercentage> {
        return this._http.get<AtmTargetPercentage>(`${this.coreApiPath}/atm/${type}/${request.id}/target`);
    }

    getAtmV2(type: AdjustedTargetMarginLevelType, request: AtmTargetPercentage): Observable<AtmTargetPercentage> {
        let params = new HttpParams();

        if (request.professionId) {
            params = params.set('professionId', request.professionId);
        }

        if (request.expertiseId) {
            params = params.set('expertiseId', request.expertiseId);
        }

        let requestType: string;
        if (type === AdjustedTargetMarginLevelType.Vms) {
            requestType = 'vms';
        } else if (type === AdjustedTargetMarginLevelType.System) {
            requestType = 'system';
        } else if (type === AdjustedTargetMarginLevelType.Facility) {
            requestType = 'facilities';
        }

        return this._http.get<AtmTargetPercentage>(`${this.coreApiPath}/atm/${requestType}/${request.id}/target`, {
            params
        });
    }

    getAtmWithProfession(type: string, request: AtmTargetPercentage): Observable<AtmTargetPercentage> {
        return this._http.get<AtmTargetPercentage>(
            `${this.coreApiPath}/atm/${type}/${request.id}/target?professionId=${request.professionId}`
        );
    }

    getAtmWithProfessionExpertise(type: string, request: AtmTargetPercentage): Observable<AtmTargetPercentage> {
        return this._http.get<AtmTargetPercentage>(
            `${this.coreApiPath}/atm/${type}/${request.id}/target?professionId=${request.professionId}&expertiseId=${request.expertiseId}`
        );
    }

    getJobAtm(jobId: number): Observable<AtmTargetPercentage> {
        return this._http.get<AtmTargetPercentage>(`${this.coreApiPath}/atm/jobs/${jobId}/target`);
    }

    saveFacilityTarget(atmPercentage: AtmTargetPercentage): Observable<any> {
        return this._http.post(`${this.coreApiPath}/atm/facility-target`, atmPercentage);
    }

    saveSystemTarget(atmPercentage: AtmTargetPercentage): Observable<any> {
        return this._http.post(`${this.coreApiPath}/atm/system-target`, atmPercentage);
    }

    saveVmsTarget(atmPercentage: AtmTargetPercentage): Observable<any> {
        return this._http.post(`${this.coreApiPath}/atm/vms-target`, atmPercentage);
    }

    saveJobTarget(atmPercentage: AtmTargetPercentage): Observable<any> {
        return this._http.post(`${this.coreApiPath}/atm/job-target`, atmPercentage);
    }
}
